import { useAtomValue } from "jotai";
import { type FunctionComponent, useCallback } from "react";
import { isFacebookUserAgent } from "scmp-sdk";

import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import { PageType } from "shared/lib/page-type";

import type { FooterVariant } from "scmp-app/components/app-footer";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { Event as Ga4Event } from "scmp-app/components/tracking/google-analytics-4/types";
import { rosettaAtom } from "scmp-app/lib/rosetta";

import { FooterDownloadAppWidget } from "./download-app-widget";
import { FooterNewsletter } from "./newsletter";
import {
  Container,
  Content,
  Copyright,
  FooterDownloadAppWidgetContainer,
  FooterNewsletterContainer,
  FooterSubscriptionWidgetContainer,
  Header,
  StyledFooterLogo,
  StyledFooterSitemap,
  StyledFooterSocialMedia,
  Wrapper,
} from "./styles";
import { FooterSubscriptionWidget } from "./subscription-widget";

export type Props = {
  className?: string;
  variant?: FooterVariant.Dark;
};

export const Footer: FunctionComponent<Props> = ({ className, variant }) => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isScmpSubscriber = asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false;
  const subscriberPhase = asyncRosettaState?.result?.tracking?.ga4.subscriberPhase;
  const currentPageType = useCurrentPageType();

  const subscribeButtonImpressionGA4Event = useCallback<() => Ga4Event>(
    () => ({
      action: "imp",
      category: "subs",
      customized_parameters: {
        call_to_action: "subscribe",
        subscriber_phase: subscriberPhase,
        trigger_point: (() => {
          switch (currentPageType) {
            case PageType.Section:
              return "section footer";
            default:
              return "homepage footer";
          }
        })(),
      },
      subcategory: "entry_point",
    }),
    [subscriberPhase, currentPageType],
  );
  const { captureTrackImpressionEventTargetElement: captureSubscribeButtonImpressionEvent } =
    useTrackImpressionEventByIntersection({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: subscribeButtonImpressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });
  const appDownloadWidgetImpressionGA4Event = useCallback<() => Ga4Event>(
    () => ({
      action: "imp",
      category: "appdl",
      customized_parameters: {
        page_type: currentPageType,
        position: "footer",
        ...(isFacebookUserAgent() && { inapp_browser: "fb" }),
      },
      subcategory: "",
    }),
    [currentPageType],
  );
  const { captureTrackImpressionEventTargetElement: captureAppDownloadWidgetImpressionEvent } =
    useTrackImpressionEventByIntersection({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: appDownloadWidgetImpressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const handleClick = () => {};

  return (
    <Container $variant={variant} className={className}>
      <Wrapper $variant={variant}>
        <Header>
          <StyledFooterLogo onClick={handleClick} variant={variant} />
          <StyledFooterSocialMedia onClick={handleClick} variant={variant} />
        </Header>
        <Content $isScmpSubscriber={isScmpSubscriber}>
          <StyledFooterSitemap onClick={handleClick} />
          {!isScmpSubscriber && (
            <FooterSubscriptionWidgetContainer ref={captureSubscribeButtonImpressionEvent}>
              <FooterSubscriptionWidget onClick={handleClick} />
            </FooterSubscriptionWidgetContainer>
          )}
          <FooterNewsletterContainer>
            <FooterNewsletter onClick={handleClick} />
          </FooterNewsletterContainer>
          <FooterDownloadAppWidgetContainer ref={captureAppDownloadWidgetImpressionEvent}>
            <FooterDownloadAppWidget onClick={handleClick} variant={variant} />
          </FooterDownloadAppWidgetContainer>
        </Content>
        <Copyright>
          Copyright © {new Date().getFullYear()} South China Morning Post Publishers Ltd. All
          rights reserved.
        </Copyright>
      </Wrapper>
    </Container>
  );
};

Footer.displayName = "Footer";
